import { useState } from 'react';
import { DECISION_LABELS } from '@ward/core';
import { api, queryClient, useApi } from '../api';
import { ConfirmDialog } from '../ui/confirm';
import { TEMPLATES } from './Policies';
import type { ProtectionSettingsResponse } from './protect/common';

/**
 * "Turn on recommended protection" (Get started checklist): one confirmed action that
 *  - turns web threat protection on in Block mode and download protection on (blocking known-malicious sources),
 *  - creates (or re-enables) three policy templates in Warn: sensitive data to personal AI, unreviewed AI tools,
 *    confidential uploads to personal cloud storage.
 * Everything goes through the existing APIs (PUT /protection/settings, POST /policies, PATCH /policies/:id/enabled),
 * so each change is permission-checked, audited and published exactly as when done by hand.
 */

const RECOMMENDED: Array<{ template: keyof typeof TEMPLATES; name: string }> = [
  { template: 'personal-ai', name: 'Warn before sensitive data goes to personal AI' },
  { template: 'unknown-ai', name: 'Warn on submissions to unreviewed AI tools' },
  { template: 'personal-cloud', name: 'Warn before confidential uploads to personal cloud storage' },
];

interface PolicyItem { id: string; name: string; enabled: boolean; action: string }

type Step =
  | { kind: 'web' | 'downloads'; label: string; change: string | null }
  | { kind: 'policy'; label: string; change: string | null; create?: (typeof RECOMMENDED)[number]; enableId?: string };

/** What would change; `change: null` means already in place. Exported for tests. */
export function recommendedPlan(settings: ProtectionSettingsResponse['settings'], policies: PolicyItem[]): Step[] {
  const web = settings.web as { enabled: boolean; mode: string };
  const dl = settings.downloads as { enabled: boolean; blockKnownMalicious: boolean };
  const steps: Step[] = [
    { kind: 'web', label: 'Web threat protection: Block', change: web.enabled && web.mode === 'block' ? null : web.enabled ? `${web.mode === 'monitor' ? 'Monitor only' : 'Warn'} → Block` : 'Off → Block' },
    { kind: 'downloads', label: 'Download protection: block known malicious sources', change: dl.enabled && dl.blockKnownMalicious ? null : 'turned on' },
  ];
  for (const r of RECOMMENDED) {
    const t = TEMPLATES[r.template]!;
    const mine = policies.find((p) => p.name === r.name);
    // An enabled policy from the original (stricter) template already covers it.
    const original = policies.find((p) => p.name === t.form.name && p.enabled);
    const label = `Policy “${r.name}” (Warn)`;
    if (mine?.enabled) steps.push({ kind: 'policy', label, change: null });
    else if (original) steps.push({ kind: 'policy', label: `Policy “${original.name}” (${DECISION_LABELS[original.action as keyof typeof DECISION_LABELS] ?? original.action})`, change: null });
    else if (mine) steps.push({ kind: 'policy', label, change: 'turned on', enableId: mine.id });
    else steps.push({ kind: 'policy', label, change: 'created', create: r });
  }
  return steps;
}

async function apply(steps: Step[]) {
  const web = steps.find((s) => s.kind === 'web')!;
  const dl = steps.find((s) => s.kind === 'downloads')!;
  if (web.change || dl.change) {
    await api('/protection/settings', { method: 'PUT', body: { ...(web.change ? { web: { enabled: true, mode: 'block' } } : {}), ...(dl.change ? { downloads: { enabled: true, blockKnownMalicious: true } } : {}) } });
  }
  for (const s of steps) {
    if (s.kind !== 'policy' || !s.change) continue;
    if (s.enableId) await api(`/policies/${s.enableId}/enabled`, { method: 'PATCH', body: { enabled: true } });
    else if (s.create) {
      const f = TEMPLATES[s.create.template]!.form;
      await api('/policies', {
        method: 'POST',
        body: { name: s.create.name, description: 'Recommended protection, turned on from the Get started checklist.', enabled: true, priority: 100, conditions: f.conditions, action: 'warn', options: f.options },
      });
    }
  }
}

export function RecommendedProtection({ onClose }: { onClose: () => void }) {
  const settings = useApi<ProtectionSettingsResponse>('/protection/settings');
  const policies = useApi<{ items: PolicyItem[] }>('/policies');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [done, setDone] = useState(false);
  const steps = settings.data && policies.data ? recommendedPlan(settings.data.settings, policies.data.items) : null;
  const pending = steps?.filter((s) => s.change) ?? [];
  const run = async () => {
    setBusy(true);
    setError(null);
    try {
      await apply(steps!);
      setDone(true);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
      await queryClient.invalidateQueries();
    }
  };
  if (done) {
    return (
      <ConfirmDialog title="Recommended protection is on" confirmLabel="Done" onConfirm={onClose} onClose={onClose}>
        <p>Published. Browsers apply the new policy and protection settings at their next check (within a few minutes).</p>
      </ConfirmDialog>
    );
  }
  return (
    <ConfirmDialog title="Turn on recommended protection?" confirmLabel={pending.length ? 'Turn on and publish' : 'Close'} busy={busy || !steps} error={error ?? settings.error ?? policies.error}
      onConfirm={() => (pending.length ? void run() : onClose())} onClose={onClose}>
      {!steps ? <p className="sub">Reading current settings…</p> : (
        <>
          <ul className="m-0 mb-12 indent" aria-label="Changes">
            {steps.map((s) => <li key={s.label}>{s.label} — <b>{s.change ?? 'already on'}</b></li>)}
          </ul>
          <p className="explain"><b>Impact:</b> pages and downloads from known malicious sources are stopped. Sensitive data going to personal AI or personal cloud storage, and prompts to unreviewed AI tools, show a warning people can continue past — each is recorded as an event. Nothing else is blocked. Every change is recorded in the audit log, and each can be changed later under Policies and Protect.</p>
          {settings.data?.threatProtection === 'off' && <p className="explain">Threat protection is off on this server: the web and download settings are saved but not applied until the operator turns it on.</p>}
        </>
      )}
    </ConfirmDialog>
  );
}
